import { IOrder } from '../types/interfaces';
import SectionHeader from '../../../components/SectionHeader';
import { useTranslation } from 'react-i18next';
import { Fragment } from 'react/jsx-runtime';
import FormUpload from '../../../components/formInputs/FormUpload';
import FieldWrapper from '../../../components/formInputs/FieldWrapper';
import { getMedia } from '../../../utils/HelperFunctions';

export default function SingleOrderDetails({ data }: { data: IOrder }) {
  const { t, i18n } = useTranslation();

  const sectionsData = [
    {
      name: t('main_information'),
      rightComp: (
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <span>{!data?.paid_amount ? t("awaiting-payment") : (data?.order_value - data?.paid_amount) ? t("partially-paid") : t("fully-paid")}</span>
          <span
            className={`badge ${data?.status === 'Confirmed' || data?.status === 'Finished' || data?.status === 'Paid And Finished' ? 'completed' : data?.status === 'Proccessing' ? 'current' : data?.status === 'Pending Cancellation' ? 'pending' : 'cancelled'}`}
          >
            {data?.customer_refused_vehicle ? t("customer_refused_vehicle") : data?.status}
          </span>
        </div>
      ),
      items: [
        { title: t('order_id'), value: data?.id },
        { title: t('order_date'), value: data?.date },
        { title: t('model'), value: data?.vehicle?.variant?.vehicle_model?.name },
        { title: t('variant'), value: data?.vehicle?.variant?.display_name?.[i18n.language as 'en' | 'ar'] },
        { title: t('chassis'), value: data?.vehicle?.chassis },
        { title: t('amount_paid'), value: data?.paid_amount?.toLocaleString('en-US') },
        { title: t('order_value'), value: data?.order_value?.toLocaleString('en-US') },
        { title: t('remaining_amount'), value: (data?.order_value - data?.paid_amount)?.toLocaleString('en-US') }
      ]
    },
    data?.assigned_sales && {
      name: t("assigned_sales"),
      items: [
        { title: t('name'), value: (data?.assigned_sales?.first_name || "") + ' ' + (data?.assigned_sales?.last_name || "") },
        { title: t('mobile'), value: data?.assigned_sales?.mobile },
        { title: t('email'), value: data?.assigned_sales?.email }
      ]
    },
    {
      name: t('customer_information'),
      items: [
        { title: t('first_name'), value: data?.user?.first_name },
        { title: t('last_name'), value: data?.user?.last_name },
        { title: t('email'), value: data?.user?.email },
        { title: t('mobile'), value: data?.user?.mobile },
        { title: t('national_id'), value: data?.user?.national_id },
        { title: t('city'), value: data?.user?.address?.city },
        { title: t('area'), value: data?.user?.address?.area },
        { title: t('street'), value: data?.user?.address?.street?.split(',')?.[0]?.trim() },
        { title: t('building_no'), value: data?.user?.address?.street?.split(',')?.[1]?.trim() },
        { title: t('apartment_no'), value: data?.user?.address?.street?.split(',')?.[2]?.trim() },
      ]
    },
    {
      name: t('vehicle_details'),
      items: [
        { title: t('brand'), value: data?.vehicle?.variant?.vehicle_model?.vehicle_make?.name },
        { title: t('model'), value: data?.vehicle?.variant?.vehicle_model?.name },
        { title: t('variant'), value: data?.vehicle?.variant?.display_name?.[i18n.language as 'en' | 'ar'] },
        { title: t('chassis'), value: data?.vehicle?.chassis },
        { title: t('exterior_color'), value: data?.vehicle?.exterior_color?.display_name?.[i18n.language as 'en' | 'ar'] },
        { title: t('interior_color'), value: data?.vehicle?.interior_color?.display_name?.[i18n.language as 'en' | 'ar'] },
        { title: t('warranty'), value: data?.warranty?.title?.[i18n.language as 'en' | 'ar'] },
        { title: t('warranty_price'), value: data?.warranty_price?.toLocaleString('en-US') }
      ]
    },
    {
      name: t('delivery_details'),
      items: [
        { title: t('delivery_type'), value: data?.additional_details?.delivery_option ? t('home_delivery') : t('showroom') },
        ...(data?.additional_details?.delivery_option ? [
          { title: t('city'), value: data?.city?.name?.[i18n.language as 'en' | 'ar'] },
          { title: t('delivery_option'), value: data?.additional_details?.delivery_option?.[i18n.language as 'en' | 'ar'] },
          { title: t('area'), value: data?.address?.split(',')?.[0]?.trim() },
          { title: t('street'), value: data?.address?.split(',')?.[1]?.trim() },
          { title: t('building_no'), value: data?.address?.split(',')?.[2]?.trim() },
          { title: t('apartment_no'), value: data?.address?.split(',')?.[3]?.trim() },
        ] : []),
        ...(data?.showroom ? [
          { title: t('city'), value: data?.showroom?.city?.name?.[i18n.language as 'en' | 'ar'] },
          { title: t('showroom'), value: data?.showroom?.name?.[i18n.language as 'en' | 'ar'] },
        ] : [])
      ]
    }
  ].filter(Boolean);

  const nationalId = getMedia(data?.media, 'national_id_file')?.url;
  const drivingLicense = getMedia(data?.media, 'driving_license')?.url;

  return (
    <Fragment>
      {sectionsData?.map((section) => (
        <SectionHeader title={section?.name} rightComp={section?.rightComp}>
          <div className="order-details">
            {section?.items?.map((item, index) => (
              <div className="order-details_item" key={`order-details-item-${index}`}>
                {item?.title && (
                  <Fragment>
                    <span className="order-details_item_title">{item?.title}:</span>
                    <span className="order-details_item_value">{item?.value || '---'}</span>
                  </Fragment>
                )}
              </div>
            ))}
          </div>
        </SectionHeader>
      ))}
      <SectionHeader title={t('files')}>
        <div className="inputs_group">
          <FieldWrapper>
            <FormUpload label={`${t('customer_national_id')}`} value={nationalId || ''} name="national_id_file" file disabled />
          </FieldWrapper>
          <FieldWrapper>
            <FormUpload label={`${t('driving_license')}`} value={drivingLicense || ''} name="driving_license" file disabled />
          </FieldWrapper>
        </div>
      </SectionHeader>
    </Fragment>
  );
}
